import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import room from '../assets/single.jpg'; // Fallback image
import {
  FaWifi,
  FaConciergeBell,
  FaParking,
  FaSuitcaseRolling,
  FaSpa,
  FaUtensils,
  FaSwimmingPool,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaClock,
  FaStar,
  FaPercent,
  FaFire,
  FaInfoCircle,
  FaTag,
  FaCrown,
} from 'react-icons/fa';
import { FiLogOut, FiArrowRight, FiCheck } from 'react-icons/fi';
import { GiPartyPopper, GiDesk } from 'react-icons/gi';
import {
  MdRoomService,
  MdUpgrade,
  MdLocalOffer,
  MdOutlineBed,
  MdSpa,
  MdRestaurant,
  MdFitnessCenter,
} from 'react-icons/md';
import { BsTaxiFrontFill, BsClock } from 'react-icons/bs';

// Environment variable
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const Welcome = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [roomType, setRoomType] = useState('');
  const [bookingStatus, setBookingStatus] = useState('');
  const [hotelInfo, setHotelInfo] = useState({
    name: '',
    address: '',
    phone: '',
    homepageUrl: '',
    logoUrl: '',
  });

  // State for API data
  const [amenityItems, setAmenityItems] = useState([]);
  const [enhanceStayData, setEnhanceStayData] = useState({
    food: [],
    amenities: [],
    room_services: [],
  });
  const [loading, setLoading] = useState({
    amenityItems: true,
    enhanceStay: true,
  });
  const [error, setError] = useState({
    amenityItems: null,
    enhanceStay: null,
  });

  useEffect(() => {
    const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
    const hotelPropertyCache = JSON.parse(localStorage.getItem('hotelPropertyCache'));

    // Get assigned rooms from localStorage
    const assignedRooms = JSON.parse(localStorage.getItem('assignedRooms'));

    // Get details from bookingDetails
    if (bookingDetails) {
      const primaryBooking =
        bookingDetails.primaryBooking || (bookingDetails.bookings && bookingDetails.bookings[0]);

      if (primaryBooking) {
        setUserName(primaryBooking.guest_name?.split(' ')[0] || 'Guest');
        setBookingStatus(primaryBooking.booking_status || '');

        // Get room number - priority order:
        // 1. From primaryBooking.room.room_number
        // 2. From assignedRooms array (matching by booking_id)
        // 3. Default to "Not Assigned"
        let roomNum = 'Not Assigned';

        // First try from primaryBooking.room
        if (primaryBooking.room && primaryBooking.room.room_number) {
          roomNum = primaryBooking.room.room_number;
        }
        // If not found, try from assignedRooms
        else if (assignedRooms && Array.isArray(assignedRooms)) {
          // Find room assignment by booking_id
          const roomAssignment = assignedRooms.find(
            (room) => room.booking_id === primaryBooking.booking_id
          );

          if (roomAssignment && roomAssignment.room_number) {
            roomNum = roomAssignment.room_number;
          }
        }

        setRoomNumber(roomNum);

        // Get room type name from room_type object
        if (primaryBooking.room_type) {
          setRoomType(primaryBooking.room_type.name || '');
        }
        // If not found in primaryBooking, try from assignedRooms
        else if (assignedRooms && Array.isArray(assignedRooms)) {
          const roomAssignment = assignedRooms.find(
            (room) => room.booking_id === primaryBooking.booking_id
          );

          if (roomAssignment && roomAssignment.room_type) {
            setRoomType(roomAssignment.room_type);
          }
        }
      }
    }

    // Get hotel info from cache
    if (hotelPropertyCache && hotelPropertyCache.data) {
      setHotelInfo({
        name: hotelPropertyCache.data.name || 'The Pagoda Hotel',
        address:
          hotelPropertyCache.data.address || 'Taluka Khed Chiplun Area, Lote, Maharashtra, 415722',
        phone: hotelPropertyCache.data.phone || '86987 32336',
        homepageUrl: hotelPropertyCache.data.homepage_url || '',
        logoUrl: hotelPropertyCache.data.logo_url || '',
      });
    }

    // Fetch API data
    fetchAmenityItems();
    fetchEnhanceStayData();

    // Handle window resize for responsive adjustments
    const handleResize = () => {
      // You can add any responsive logic here if needed
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch amenity items API
  const fetchAmenityItems = async () => {
    try {
      setLoading((prev) => ({ ...prev, amenityItems: true }));
      const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityitems/checkin`, {
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAmenityItems(data);
      setError((prev) => ({ ...prev, amenityItems: null }));
    } catch (error) {
      console.error('Error fetching amenity items:', error);
      setError((prev) => ({ ...prev, amenityItems: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, amenityItems: false }));
    }
  };

  // Fetch enhance your stay API
  const fetchEnhanceStayData = async () => {
    try {
      setLoading((prev) => ({ ...prev, enhanceStay: true }));
      const response = await fetch(`${BQ_BASE_URL}/bq/api/enhance-your-stay`, {
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setEnhanceStayData(data);
      setError((prev) => ({ ...prev, enhanceStay: null }));
    } catch (error) {
      console.error('Error fetching enhance stay data:', error);
      setError((prev) => ({ ...prev, enhanceStay: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, enhanceStay: false }));
    }
  };

  // Function to determine navigation link based on item type
  const getNavigationLink = (item) => {
    // For amenity items from first API
    if (
      item.serviceid === 2 ||
      item.name?.toLowerCase().includes('food') ||
      item.name?.toLowerCase().includes('mini-fridge')
    ) {
      return '/reservation/options/booking/welcome/food-service';
    }

    // For room services
    if (
      item.serviceid === 1 ||
      item.categoryid === 1 ||
      item.name?.toLowerCase().includes('bed') ||
      item.name?.toLowerCase().includes('pillow')
    ) {
      return '/reservation/options/booking/welcome/room-service';
    }

    // Default to paid amenities
    return '/reservation/options/booking/welcome/paid-amenities';
  };

  // Get icon based on service type
  const getServiceIcon = (serviceType, title) => {
    const titleLower = title?.toLowerCase() || '';

    if (titleLower.includes('bed') || titleLower.includes('pillow')) return <MdOutlineBed />;
    if (titleLower.includes('spa') || titleLower.includes('treatment')) return <MdSpa />;
    if (
      titleLower.includes('food') ||
      titleLower.includes('buffet') ||
      titleLower.includes('dining') ||
      titleLower.includes('breakfast') ||
      titleLower.includes('dinner')
    )
      return <MdRestaurant />;
    if (titleLower.includes('gym') || titleLower.includes('fitness') || titleLower.includes('yoga'))
      return <MdFitnessCenter />;
    if (titleLower.includes('pool') || titleLower.includes('swimming')) return <FaSwimmingPool />;
    if (titleLower.includes('parking') || titleLower.includes('valet')) return <FaParking />;
    if (titleLower.includes('conference') || titleLower.includes('room')) return <GiDesk />;

    switch (serviceType) {
      case 'food':
        return <MdRestaurant />;
      case 'amenity':
        return <FaCrown />;
      case 'room_service':
        return <MdRoomService />;
      default:
        return <FaStar />;
    }
  };

  // Calculate discount percentage
  const calculateDiscount = (basePrice, urgentPrice) => {
    if (!urgentPrice || urgentPrice <= basePrice) return 0;
    const discount = ((urgentPrice - basePrice) / urgentPrice) * 100;
    return Math.round(discount);
  };

  // Combine both APIs data for the Exclusive Offers section
  const getAllOffers = () => {
    const offers = [];

    // Add amenity items from first API
    amenityItems.forEach((item) => {
      const discount = calculateDiscount(item.baseprice, item.urgentprice);
      offers.push({
        id: item.id,
        title: item.name,
        description: `${item.price_type} • ${item.refundable ? 'Refundable' : 'Non-refundable'}`,
        basePrice: item.baseprice,
        urgentPrice: item.urgentprice,
        image: item.imageurl,
        tag: item.categoryid === 4 ? 'FACILITY' : 'AMENITY',
        discount,
        discountText: discount > 0 ? `Save ${discount}%` : '',
        itemType: 'amenity',
        serviceid: item.serviceid,
        categoryid: item.categoryid,
        hasDiscount: item.urgentprice > item.baseprice,
        priceType: item.price_type,
        refundable: item.refundable,
        originalItem: item,
      });
    });

    // Add food items from second API
    enhanceStayData.food.forEach((item) => {
      offers.push({
        id: item.id,
        title: item.title,
        description: item.description,
        basePrice: item.price,
        urgentPrice: item.price,
        image: item.image,
        tag: 'FOOD',
        discount: 0,
        discountText: '',
        itemType: 'food',
        priceType: item.price_type,
        refundable: item.refundable,
        originalItem: item,
      });
    });

    // Add amenities from second API
    enhanceStayData.amenities.forEach((item) => {
      const discount = calculateDiscount(item.base_price, item.urgent_price);
      offers.push({
        id: item.id,
        title: item.name,
        description: `${item.price_type} • ${item.refundable ? 'Refundable' : 'Non-refundable'}`,
        basePrice: item.base_price,
        urgentPrice: item.urgent_price,
        image: item.image_url,
        tag: 'FACILITY',
        discount,
        discountText: discount > 0 ? `Save ${discount}%` : '',
        itemType: 'amenity',
        hasDiscount: item.urgent_price > item.base_price,
        priceType: item.price_type,
        refundable: item.refundable,
        originalItem: item,
      });
    });

    // Add room services from second API
    enhanceStayData.room_services.forEach((item) => {
      const discount = calculateDiscount(item.base_price, item.urgent_price);
      offers.push({
        id: item.id,
        title: item.name,
        description: `${item.price_type} • ${item.refundable ? 'Refundable' : 'Non-refundable'}`,
        basePrice: item.base_price,
        urgentPrice: item.urgent_price,
        image: item.image_url,
        tag: 'SPA & WELLNESS',
        discount,
        discountText: discount > 0 ? `Save ${discount}%` : '',
        itemType: 'room_service',
        hasDiscount: item.urgent_price > item.base_price,
        priceType: item.price_type,
        refundable: item.refundable,
        originalItem: item,
      });
    });

    return offers;
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/reservation/options');
  };

  // Handle card click navigation
  const handleCardClick = (link) => {
    navigate(link);
  };

  const promotionSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: true,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
    ],
  };

  const quickActions = [
    {
      title: 'Reschedule Stay',
      icon: <FaClock className="text-xl sm:text-2xl text-gray-950" />,
      description: 'Adjust your reservation dates',
      link: '/reservation/options/update-checkout-date',
      buttonText: 'Modify Dates',
      color: 'bg-gray-100 border-gray-100',
    },
    {
      title: 'Room Upgrade',
      icon: <MdUpgrade className="text-xl sm:text-2xl text-gray-950" />,
      description: 'Enhance your experience',
      link: '/reservation/options/update-room-type',
      buttonText: 'Upgrade Now',
      color: 'bg-gray-100 border-gray-100',
    },
    {
      title: 'Amenities',
      icon: <GiDesk className="text-xl sm:text-2xl text-gray-950" />,
      description: 'Book Your Amenities',
      link: '/reservation/options/booking/welcome/paid-amenities',
      buttonText: 'View Amenities',
      color: 'bg-gray-100 border-gray-100',
    },
    {
      title: 'Room Service',
      icon: <MdRoomService className="text-xl sm:text-2xl text-gray-950" />,
      description: 'Food & room service',
      link: '/reservation/options/booking/welcome/room-service',
      buttonText: 'View Services',
      color: 'bg-gray-100 border-gray-100',
    },
  ];

  const services = [
    {
      icon: <FaSuitcaseRolling className="text-xl sm:text-2xl" />,
      name: 'Luggage Service',
      description: '24/7 luggage assistance',
    },
    {
      icon: <FaWifi className="text-xl sm:text-2xl" />,
      name: 'High-Speed WiFi',
      description: 'Complimentary high-speed internet',
    },
    {
      icon: <FaSpa className="text-xl sm:text-2xl" />,
      name: 'Spa & Wellness',
      description: 'Relaxing treatments available',
    },
    {
      icon: <FaUtensils className="text-xl sm:text-2xl" />,
      name: '24/7 Dining',
      description: 'Round-the-clock restaurant',
    },
    {
      icon: <FaParking className="text-xl sm:text-2xl" />,
      name: 'Valet Parking',
      description: 'Complimentary parking service',
    },
    {
      icon: <FaSwimmingPool className="text-xl sm:text-2xl" />,
      name: 'Infinity Pool',
      description: 'Heated pool with view',
    },
    {
      icon: <BsTaxiFrontFill className="text-xl sm:text-2xl" />,
      name: 'Exclusive Taxi Service',
      description: 'Safe rides from hotel',
    },
    {
      icon: <GiPartyPopper className="text-xl sm:text-2xl" />,
      name: 'Event Services',
      description: 'Venue booking & planning',
    },
  ];

  const hotelContactInfo = [
    { icon: <FaPhoneAlt />, text: hotelInfo.phone, label: 'Call Us' },
    { icon: <FaMapMarkerAlt />, text: hotelInfo.address, label: 'Location' },
    { icon: <FaConciergeBell />, text: 'Concierge 24/7', label: 'Services' },
  ];

  // Use homepage URL from hotel property cache, fallback to default image
  const heroImage = hotelInfo.homepageUrl || room;

  // Get all offers from both APIs
  const allOffers = getAllOffers();

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-300 to-gray-500 text-gray-900 overflow-x-hidden">
      {/* Hero Section - Responsive height */}
      <div className="relative w-full h-[60vh] sm:h-[70vh] lg:h-[85vh] overflow-hidden">
        <div className="relative h-full">
          <img src={heroImage} alt="Pagoda" className="object-cover w-full h-full" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent">
            <div className="container mx-auto h-full flex items-center px-4 sm:px-6 lg:px-8">
              <div className="text-white max-w-2xl w-full">
                <div className="flex items-center gap-4 mb-4 sm:mb-6">
                  <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-amber-400 to-transparent"></div>
                  <span className="text-xs sm:text-sm tracking-widest text-amber-200 truncate">
                    WELCOME TO {hotelInfo.name}
                  </span>
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light mb-4 sm:mb-6 capitalize tracking-tight">
                  Welcome Back,
                  <br />
                  <span className="font-medium text-amber-100">{userName || 'Guest'}!</span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-6 sm:mb-10 max-w-lg">
                  {bookingStatus === 'Hard'
                    ? "We're delighted to have you as our guest"
                    : `Your ${roomType} suite is ready for your checkin, please collect your keys from front desk`}
                </p>
                <div className="flex flex-wrap gap-3 sm:gap-6">
                  <div className="bg-white/10 backdrop-blur-sm px-4 sm:px-8 py-3 sm:py-4 rounded-xl border border-white/20 min-w-[140px] sm:min-w-[180px]">
                    <p className="text-xs sm:text-sm text-gray-300 uppercase tracking-wider">
                      ROOM
                    </p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">
                      {roomNumber || 'N/A'}
                    </p>
                  </div>
                  {roomType && (
                    <div className="bg-white/10 backdrop-blur-sm px-4 sm:px-8 py-3 sm:py-4 rounded-xl border border-white/20 min-w-[140px] sm:min-w-[180px]">
                      <p className="text-xs sm:text-sm text-gray-300 uppercase tracking-wider">
                        ROOM TYPE
                      </p>
                      <p className="text-base sm:text-lg lg:text-xl font-semibold mt-1 sm:mt-2 truncate">
                        {roomType}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-end items-center">
          <button
            onClick={handleLogout}
            className="text-white hover:text-gray-100 transition duration-300 flex items-center gap-1 sm:gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl hover:bg-white/20 border border-white/20 text-sm sm:text-base"
          >
            Logout <FiLogOut size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span className="font-medium hidden xs:inline">Logout</span>
          </button>
        </nav>
      </div>

      {/* Quick Actions - Responsive grid */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-2 sm:mb-3">
              Quick Actions
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Manage your stay with these convenient options
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {quickActions.map((action, index) => (
              <div
                key={index}
                onClick={() => handleCardClick(action.link)}
                className={`${action.color} border rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 transform group cursor-pointer`}
              >
                <div className="flex items-start justify-between mb-4 sm:mb-6">
                  <div className="p-3 sm:p-4 rounded-xl bg-white shadow-md group-hover:scale-110 transition-transform">
                    {action.icon}
                  </div>
                  <FiArrowRight className="text-gray-400 group-hover:translate-x-2 transition-transform" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                  {action.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                  {action.description}
                </p>
                <div className="inline-flex items-center font-medium text-gray-900 hover:text-black transition group-hover:gap-3 text-sm sm:text-base">
                  {action.buttonText}
                  <FiArrowRight className="ml-2 group-hover:ml-4 transition-all" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exclusive Offers - Enhanced Cards */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8 sm:mb-12">
            <div className="mb-4 lg:mb-0">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500">
                  <FaTag className="text-white text-xs sm:text-sm" />
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900">
                  Exclusive Offers
                </h2>
              </div>
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl">
                Special promotions and premium amenities for our valued guests
              </p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100">
              <FaFire className="text-orange-500 text-xs sm:text-sm" />
              <span className="font-medium text-gray-900 text-xs sm:text-sm">
                Limited Time Only
              </span>
            </div>
          </div>

          {loading.amenityItems || loading.enhanceStay ? (
            <div className="text-center py-12 sm:py-16">
              <div className="inline-block animate-spin rounded-full h-8 sm:h-12 w-8 sm:w-12 border-b-2 border-amber-500"></div>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base lg:text-lg text-gray-600">
                Loading exclusive offers...
              </p>
            </div>
          ) : error.amenityItems || error.enhanceStay ? (
            <div className="text-center py-12 sm:py-16">
              <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-red-100 mb-3 sm:mb-4">
                <FaInfoCircle className="text-red-500 text-xl sm:text-2xl" />
              </div>
              <p className="text-red-600 text-sm sm:text-base lg:text-lg">
                Error loading offers. Please try again later.
              </p>
            </div>
          ) : allOffers.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-gray-100 mb-3 sm:mb-4">
                <FaInfoCircle className="text-gray-500 text-xl sm:text-2xl" />
              </div>
              <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                No exclusive offers available at the moment.
              </p>
            </div>
          ) : (
            <div className="relative px-0 sm:px-2 lg:px-4">
              <Slider {...promotionSettings}>
                {allOffers.map((offer, index) => {
                  const discount = calculateDiscount(offer.basePrice, offer.urgentPrice);
                  const ServiceIcon = getServiceIcon(offer.itemType, offer.title);

                  return (
                    <div key={`${offer.id}-${index}`} className="px-2 sm:px-3 pb-2">
                      <div
                        onClick={() => handleCardClick(getNavigationLink(offer.originalItem))}
                        className="group bg-white rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-300 border border-gray-100 h-full hover:shadow-xl hover:-translate-y-1 cursor-pointer"
                      >
                        {/* Image Container with Overlay */}
                        <div className="relative h-40 sm:h-48 md:h-56 overflow-hidden">
                          {offer.image ? (
                            <>
                              <img
                                src={offer.image}
                                alt={offer.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                            </>
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                              <div className="text-3xl sm:text-4xl text-amber-600">
                                {ServiceIcon}
                              </div>
                            </div>
                          )}

                          {/* Discount Badge */}
                          {offer.urgentPrice > offer.basePrice && (
                            <div className="absolute top-2 sm:top-4 left-2 sm:left-4">
                              <div className="flex items-center gap-1 bg-gradient-to-r from-red-500 to-orange-500 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-lg">
                                <FaFire className="text-xs" />
                                <span className="font-bold text-xs sm:text-sm">SPECIAL OFFER</span>
                              </div>
                            </div>
                          )}

                          {/* Service Type Badge */}
                          <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                            <div className="bg-black/80 backdrop-blur-sm text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-medium">
                              {offer.tag}
                            </div>
                          </div>

                          {/* Refundable Badge */}
                          {offer.refundable && (
                            <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4">
                              <div className="flex items-center gap-1 bg-green-500/90 backdrop-blur-sm text-white px-2 sm:px-3 py-1 rounded-full text-xs">
                                <FiCheck className="text-xs" />
                                <span>Refundable</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Card Content */}
                        <div className="p-4 sm:p-6">
                          {/* Title and Icon */}
                          <div className="flex items-start justify-between mb-3 sm:mb-4">
                            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 line-clamp-1 pr-2">
                              {offer.title}
                            </h3>
                            <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 flex-shrink-0">
                              <div className="text-amber-600 text-sm sm:text-base md:text-lg">
                                {ServiceIcon}
                              </div>
                            </div>
                          </div>

                          {/* Price & Action */}
                          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 sm:gap-0">
                            <div className="w-full sm:w-auto">
                              {/* Price Display */}
                              <div className="space-y-1.5">
                                {/* Main Price Row */}
                                <div className="flex flex-wrap items-baseline gap-2 sm:gap-3">
                                  {/* Current Price */}
                                  <div className="flex flex-col">
                                    <span className="text-lg sm:text-xl md:text-3xl font-bold text-gray-900">
                                      ₹{offer.basePrice}
                                    </span>
                                    <span className="text-xs text-gray-500">Current Price</span>
                                  </div>

                                  {/* Urgent Price - only show if higher */}
                                  {offer.urgentPrice > offer.basePrice && (
                                    <div className="flex flex-col items-start border-l border-gray-200 pl-2 sm:pl-3">
                                      <span className="text-sm sm:text-md text-gray-500 line-through">
                                        ₹{offer.urgentPrice}
                                      </span>
                                      <span className="text-xs text-gray-500">Urgent Price</span>
                                    </div>
                                  )}
                                </div>

                                {/* Savings Information */}
                                {offer.urgentPrice > offer.basePrice && (
                                  <div className="flex flex-col gap-1">
                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                      <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                                        <span className="text-xs font-semibold text-green-800">
                                          Save ₹{offer.urgentPrice - offer.basePrice}
                                        </span>
                                        <span className="text-xs text-green-600">
                                          (
                                          {Math.round(
                                            ((offer.urgentPrice - offer.basePrice) /
                                              offer.urgentPrice) *
                                              100
                                          )}
                                          % off)
                                        </span>
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">
                                      Book now for this special rate!
                                    </p>
                                  </div>
                                )}

                                {/* Price Type */}
                                <div className="flex items-center gap-1.5 pt-1 sm:pt-2">
                                  <BsClock className="text-xs text-gray-400" />
                                  <span className="text-xs sm:text-sm text-gray-500">
                                    {offer.priceType}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* View Details Button */}
                            <div className="w-full sm:w-auto">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(getNavigationLink(offer.originalItem));
                                }}
                                className="w-full sm:w-auto flex items-center justify-center gap-1 sm:gap-2 bg-gradient-to-r from-gray-900 to-black text-white px-3 sm:px-5 py-2 sm:py-3 rounded-xl hover:shadow-lg transition-all duration-300 hover:gap-2 sm:hover:gap-3 group/btn text-sm sm:text-base"
                              >
                                <span className="font-medium">View Details</span>
                                <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Slider>
            </div>
          )}
        </div>
      </section>

      {/* Premium Services */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 mb-4 sm:mb-6">
              <FaCrown className="text-xl sm:text-2xl text-blue-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-2 sm:mb-3">
              Premium Services
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Experience luxury with our comprehensive range of services
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border border-gray-100 text-center"
              >
                <div className="inline-flex items-center justify-center w-12 sm:w-16 md:w-20 h-12 sm:h-16 md:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                  <div className="text-blue-600 text-lg sm:text-xl md:text-2xl">{service.icon}</div>
                </div>
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base md:text-lg mb-1 sm:mb-2 md:mb-3">
                  {service.name}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hotel Information */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-6">
          <div className="grid md:grid-cols-3 gap-8 sm:gap-12">
            {hotelContactInfo.map((info, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                  <div className="text-base sm:text-lg">{info.icon}</div>
                </div>
                <h4 className="font-semibold mb-2 sm:mb-3 text-amber-100 text-base sm:text-lg">
                  {info.label}
                </h4>
                <p className="text-sm sm:text-base font-light text-gray-300 leading-relaxed px-2">
                  {info.text}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 sm:mt-12 md:mt-16 pt-8 sm:pt-12 border-t border-gray-800 text-center">
            <div className="inline-flex items-center justify-center w-16 sm:w-20 h-16 sm:h-20 rounded-full bg-gradient-to-br from-amber-500/10 to-orange-500/10 mb-4 sm:mb-6">
              <FaStar size={36} className="sm:w-[48px] sm:h-[48px] text-amber-400" />
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-light mb-3 sm:mb-4">
              {hotelInfo.name}
            </h3>
            <p className="text-sm sm:text-base lg:text-lg text-gray-400 max-w-2xl mx-auto px-4">
              Where luxury meets comfort, and every moment becomes a cherished memory
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 md:py-10 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
            <div className="text-center md:text-left">
              <p className="text-xs sm:text-sm text-gray-400">
                © {new Date().getFullYear()} {hotelInfo.name}. All rights reserved.
              </p>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-6 text-xs sm:text-sm">
              <a href="#" className="text-gray-400 hover:text-amber-400 transition">
                Privacy Policy
              </a>
              <span className="text-gray-600 hidden sm:inline">•</span>
              <a href="#" className="text-gray-400 hover:text-amber-400 transition">
                Terms of Service
              </a>
              <span className="text-gray-600 hidden sm:inline">•</span>
              <a href="#" className="text-gray-400 hover:text-amber-400 transition">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom Styles */}
      <style jsx>{`
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        /* Hide text on very small screens for logout button */
        @media (max-width: 380px) {
          .xs\:inline {
            display: none;
          }
        }

        /* Slick slider custom styles */
        .slick-prev:before,
        .slick-next:before {
          color: #000000 !important;
          opacity: 1 !important;
          font-size: 18px !important;
        }

        .slick-prev,
        .slick-next {
          width: 36px !important;
          height: 36px !important;
          background: white !important;
          border-radius: 50% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
          z-index: 10 !important;
          transition: all 0.3s ease !important;
        }

        .slick-prev:hover,
        .slick-next:hover {
          background: #f8fafc !important;
          box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15) !important;
        }

        .slick-prev {
          left: -40px !important;
        }

        .slick-next {
          right: -40px !important;
        }

        @media (max-width: 1280px) {
          .slick-prev {
            left: -30px !important;
          }
          .slick-next {
            right: -30px !important;
          }
        }

        @media (max-width: 1024px) {
          .slick-prev,
          .slick-next {
            display: none !important;
          }
        }

        .slick-dots {
          bottom: -30px !important;
        }

        .slick-dots li {
          margin: 0 3px !important;
        }

        .slick-dots li button:before {
          font-size: 8px !important;
          color: #d1d5db !important;
          opacity: 1 !important;
        }

        .slick-dots li.slick-active button:before {
          color: #f59e0b !important;
          font-size: 10px !important;
        }

        @media (max-width: 640px) {
          .slick-dots {
            bottom: -25px !important;
          }
        }

        /* Touch-friendly improvements */
        @media (hover: none) and (pointer: coarse) {
          .group:hover {
            transform: none !important;
          }

          button,
          [role='button'],
          .cursor-pointer {
            cursor: default;
            -webkit-tap-highlight-color: transparent;
          }

          .group:active {
            transform: scale(0.98);
            transition: transform 0.1s ease;
          }
        }

        /* Better scrolling on mobile */
        .overflow-x-hidden {
          -webkit-overflow-scrolling: touch;
        }

        /* Image optimization */
        img {
          -webkit-user-drag: none;
          -khtml-user-drag: none;
          -moz-user-drag: none;
          -o-user-drag: none;
        }
      `}</style>
    </div>
  );
};

export default Welcome;

// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import Slider from "react-slick";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import spa from "../assets/spa.jpg";
// import fitness from "../assets/fitness.jpg";
// import food from "../assets/food.jpg";
// import room from "../assets/single.jpg";
// import pool from "../assets/pool.webp";
// import lobby from "../assets/lobby.jpg";
// import {
//   FaWifi,
//   FaConciergeBell,
//   FaParking,
//   FaSuitcaseRolling,
//   FaSpa,
//   FaUtensils,
//   FaSwimmingPool,
//   FaCoffee,
//   FaPhoneAlt,
//   FaMapMarkerAlt,
//   FaClock,
// } from "react-icons/fa";
// import { FiLogOut, FiArrowRight } from "react-icons/fi";
// import { GiPartyPopper, GiDesk } from "react-icons/gi";
// import { MdRoomService, MdUpgrade } from "react-icons/md";

// const Welcome = () => {
//   const navigate = useNavigate();
//   const [userName, setUserName] = useState("");
//   const [roomNumber, setRoomNumber] = useState("");
//   const [lastName, setLastName] = useState("");
//   const [bookingStatus, setBookingStatus] = useState("");
//   const [hotelInfo, setHotelInfo] = useState({
//     name: "",
//     address: "",
//     phone: "",
//     logo: "",
//   });

//   useEffect(() => {
//     const bookingDetails = JSON.parse(localStorage.getItem("bookingDetails"));
//     const hotelPropertyCache = JSON.parse(
//       localStorage.getItem("hotelPropertyCache")
//     );

//     // Get room number from assignedRooms in localStorage
//     const assignedRooms = JSON.parse(localStorage.getItem("assignedRooms"));

//     if (assignedRooms && assignedRooms.length > 0) {
//       // Extract room number from the first assigned room
//       setRoomNumber(assignedRooms[0].room_number || "Not Assigned");
//     } else if (bookingDetails) {
//       // Fallback to bookingDetails if assignedRooms is not available
//       const primaryBooking =
//         bookingDetails.primaryBooking ||
//         (bookingDetails.bookings && bookingDetails.bookings[0]);

//       if (primaryBooking) {
//         setUserName(primaryBooking.guest_name.split(" ")[0]);
//         setLastName(primaryBooking.guest_name.split(" ")[1] || "");
//         setBookingStatus(primaryBooking.booking_status || "");
//         setRoomNumber(
//           primaryBooking.room ? primaryBooking.room.room_number : "Not Assigned"
//         );
//       }
//     }

//     if (hotelPropertyCache && hotelPropertyCache.data) {
//       setHotelInfo({
//         name: hotelPropertyCache.data.name || "The Pagoda Hotel",
//         address:
//           hotelPropertyCache.data.address ||
//           "Taluka Khed Chiplun Area, Lote, Maharashtra, 415722",
//         phone: hotelPropertyCache.data.phone || "86987 32336",
//         logo: hotelPropertyCache.data.logo_url || "",
//       });
//     }
//   }, []);

//   const handleLogout = () => {
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/reservation/options");
//   };

//   const carouselSettings = {
//     dots: true,
//     infinite: true,
//     speed: 500,
//     slidesToShow: 1,
//     slidesToScroll: 1,
//     autoplay: true,
//     autoplaySpeed: 5000,
//     arrows: false,
//     fade: true,
//   };

//   const promotionSettings = {
//     dots: false,
//     infinite: true,
//     speed: 500,
//     slidesToShow: 3,
//     slidesToScroll: 1,
//     autoplay: true,
//     autoplaySpeed: 4000,
//     responsive: [
//       {
//         breakpoint: 1024,
//         settings: {
//           slidesToShow: 2,
//         },
//       },
//       {
//         breakpoint: 768,
//         settings: {
//           slidesToShow: 1,
//         },
//       },
//     ],
//   };

//   const quickActions = [
//     {
//       title: "Reschedule Stay",
//       icon: <FaClock className="text-2xl" />,
//       description: "Adjust your reservation dates",
//       link: "/reservation/options/update-checkout-date",
//       buttonText: "Modify Dates",
//     },
//     {
//       title: "Room Upgrade",
//       icon: <MdUpgrade className="text-2xl" />,
//       description: "Enhance your experience",
//       link: "/reservation/options/update-room-type",
//       buttonText: "Upgrade Now",
//     },
//     {
//       title: "Amenities",
//       icon: <GiDesk className="text-2xl" />,
//       description: "Book Your Amenities",
//       link: "/reservation/options/booking/welcome/paid-amenities",
//       buttonText: "View Amenities",
//     },
//     {
//       title: "Room Service",
//       icon: <MdRoomService className="text-2xl" />,
//       description: "Order food ",
//       link: "/reservation/options/booking/welcome/room-service",
//       buttonText: "View Menu",
//     },
//   ];

//   const promotions = [
//     {
//       title: "Luxury Spa Retreat",
//       description: "15% off all treatments for hotel guests",
//       image: spa,
//     },
//     {
//       title: "Gourmet Dining",
//       description: "Chef's tasting menu with wine pairing",
//       image: food,
//     },
//     {
//       title: "Poolside Cabana",
//       description: "Private cabana rental with cocktail service",
//       image: pool,
//     },
//     {
//       title: "Fitness Center",
//       description: "Personal training sessions available",
//       image: fitness,
//     },
//     {
//       title: "Executive Lounge",
//       description: "Exclusive access with premium benefits",
//       image: lobby,
//     },
//   ];

//   const services = [
//     {
//       icon: <FaSuitcaseRolling className="text-2xl" />,
//       name: "Luggage Service",
//     },
//     { icon: <FaWifi className="text-2xl" />, name: "High-Speed WiFi" },
//     { icon: <FaSpa className="text-2xl" />, name: "Spa & Wellness" },
//     { icon: <FaUtensils className="text-2xl" />, name: "24/7 Dining" },
//     { icon: <FaParking className="text-2xl" />, name: "Valet Parking" },
//     { icon: <FaSwimmingPool className="text-2xl" />, name: "Infinity Pool" },
//     { icon: <FaCoffee className="text-2xl" />, name: "Coffee Bar" },
//     { icon: <GiPartyPopper className="text-2xl" />, name: "Event Services" },
//   ];

//   const hotelContactInfo = [
//     { icon: <FaPhoneAlt />, text: hotelInfo.phone },
//     { icon: <FaMapMarkerAlt />, text: hotelInfo.address },
//     { icon: <FaConciergeBell />, text: "Concierge available 24/7" },
//   ];

//   return (
//     <div className="min-h-screen w-full bg-white text-gray-900">
//       {/* Hero Section */}
//       <div className="relative w-full h-screen max-h-[80vh] overflow-hidden">
//         <Slider {...carouselSettings}>
//           <div className="relative h-[70vh]">
//             <img
//               src={room}
//               alt="Luxury Room"
//               className="object-cover w-full h-full"
//             />
//             <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center">
//               <div className="container mx-auto px-8 text-white">
//                 <div className="max-w-2xl">
//                   <h1 className="text-5xl font-light mb-4 text-transform: capitalize;">
//                     Welcome, {userName || "Guest"}
//                   </h1>
//                   <div className="h-px w-20 bg-white mb-6"></div>
//                   <p className="text-xl mb-8 text-transform: capitalize;">
//                     {bookingStatus === "Hard"
//                       ? "We're delighted to have you as our guest. Your comfort is our priority"
//                       : `Your room at ${hotelInfo.name} is prepared for your arrival`}
//                   </p>
//                   <button className="border border-white px-8 py-3 hover:bg-white hover:text-black transition duration-300">
//                     Explore Your Room
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="relative h-[70vh]">
//             <img src={pool} alt="Pool" className="object-cover w-full h-full" />
//             <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center">
//               <div className="container mx-auto px-8 text-white">
//                 <div className="max-w-2xl">
//                   <h1 className="text-5xl font-light mb-4">
//                     Discover Our Oasis
//                   </h1>
//                   <div className="h-px w-20 bg-white mb-6"></div>
//                   <p className="text-xl mb-8">
//                     Relax by our infinity pool with panoramic city views and
//                     premium cocktail service
//                   </p>
//                   <button className="border border-white px-8 py-3 hover:bg-white hover:text-black transition duration-300">
//                     View Pool Services
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="relative h-[70vh]">
//             <img
//               src={lobby}
//               alt="Lobby"
//               className="object-cover w-full h-full"
//             />
//             <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center">
//               <div className="container mx-auto px-8 text-white">
//                 <div className="max-w-2xl">
//                   <h1 className="text-5xl font-light mb-4">Executive Lounge</h1>
//                   <div className="h-px w-20 bg-white mb-6"></div>
//                   <p className="text-xl mb-8">
//                     Enjoy exclusive access to our executive lounge with
//                     complimentary refreshments
//                   </p>
//                   <button className="border border-white px-8 py-3 hover:bg-white hover:text-black transition duration-300">
//                     Learn About Benefits
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </Slider>

//         {/* Navigation */}
//         <nav className="absolute top-0 left-0 w-full px-8 py-6 flex justify-between items-center">
//           {/* Room Number Display */}
//           <div className="text-white bg-black bg-opacity-50 px-4 py-2 rounded-lg">
//             <span className="font-semibold text-xl">Room number: {roomNumber || "N/A"}</span>
//           </div>

//           <button
//             onClick={handleLogout}
//             className="text-white hover:text-gray-300 transition duration-300 flex items-center gap-2"
//           >
//             <FiLogOut size={20} />
//             <span>Logout</span>
//           </button>
//         </nav>
//       </div>

//       {/* Quick Actions */}
//       <section className="py-4 bg-white mb-8 ">
//         <div className="container mx-auto px-6">
//           <div className="text-center mb-12">
//             <h2 className="text-3xl font-light mb-2">Manage Your Stay</h2>
//             <div className="h-px w-16 bg-black mx-auto"></div>
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
//             {quickActions.map((action, index) => (
//               <div
//                 key={index}
//                 className="border border-gray-200 p-6 hover:shadow-lg transition duration-300"
//               >
//                 <div className="text-black mb-4">{action.icon}</div>
//                 <h3 className="text-xl font-light mb-2">{action.title}</h3>
//                 <p className="text-gray-600 mb-6">{action.description}</p>
//                 <a
//                   href={action.link}
//                   className="inline-flex items-center text-black border-b border-black pb-1 hover:border-gray-400 transition"
//                 >
//                   {action.buttonText}
//                   <FiArrowRight className="ml-2" />
//                 </a>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Current Offers */}
//       <section className="py-16 bg-gray-50">
//         <div className="container mx-auto px-6">
//           <div className="text-center mb-12">
//             <h2 className="text-3xl font-light mb-2">Current Offers</h2>
//             <div className="h-px w-16 bg-black mx-auto"></div>
//             <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
//               Enhance your stay with these exclusive promotions
//             </p>
//           </div>

//           <Slider {...promotionSettings}>
//             {promotions.map((promo, index) => (
//               <div key={index} className="px-2">
//                 <div className="relative h-64 group">
//                   <img
//                     src={promo.image}
//                     alt={promo.title}
//                     className="w-full h-full object-cover"
//                   />
//                   <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition duration-300 flex items-center justify-center p-6">
//                     <div className="text-white text-center">
//                       <h3 className="text-xl font-light mb-2">{promo.title}</h3>
//                       <p className="mb-4">{promo.description}</p>
//                       <button className="border border-white px-4 py-2 hover:bg-white hover:text-black transition">
//                         Learn More
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </Slider>
//         </div>
//       </section>

//       {/* Hotel Services */}
//       <section className="py-16 bg-white">
//         <div className="container mx-auto px-6">
//           <div className="text-center mb-12">
//             <h2 className="text-3xl font-light mb-2">Hotel Services</h2>
//             <div className="h-px w-16 bg-black mx-auto"></div>
//           </div>

//           <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
//             {services.map((service, index) => (
//               <div
//                 key={index}
//                 className="text-center p-6 hover:bg-gray-50 transition"
//               >
//                 <div className="text-black mb-4 flex justify-center">
//                   {service.icon}
//                 </div>
//                 <h3 className="font-light">{service.name}</h3>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Hotel Information */}
//       <section className="py-16 bg-black text-white">
//         <div className="container mx-auto px-6">
//           <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-12">
//             {hotelContactInfo.map((info, index) => (
//               <div
//                 key={index}
//                 className="flex items-center sm:flex-col sm:text-center sm:flex-1"
//               >
//                 <div className="mr-4 sm:mr-0 sm:mb-4 text-xl">{info.icon}</div>
//                 <p className="font-light">{info.text}</p>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Footer */}
//       <footer className="py-12 bg-gray-100">
//         <div className="container mx-auto px-6 text-center">
//           <p className="text-gray-600 mb-4">
//             © {new Date().getFullYear()} {hotelInfo.name}
//           </p>
//           <p className="text-gray-500 text-sm">
//             Designed for your comfort and convenience
//           </p>
//         </div>
//       </footer>
//     </div>
//   );
// };

// export default Welcome;
